/**
 * Hooks for the persistent system-notifications inbox (bell popover).
 *
 * The bell shows non-messaging notifications only — the AppBar MessageSquare
 * icon handles messaging unread count separately.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../api/axiosInstance';

const API_BASE = '/api/v1/notifications/inbox';

export interface InboxNotification {
  id: string;
  notification_type: string;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  data: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export const notificationsInboxKeys = {
  all: ['notifications-inbox'] as const,
  list: () => [...notificationsInboxKeys.all, 'list'] as const,
  unreadCount: () => [...notificationsInboxKeys.all, 'unread-count'] as const,
};

export function useInboxNotifications(limit = 30) {
  return useQuery({
    queryKey: notificationsInboxKeys.list(),
    queryFn: async (): Promise<InboxNotification[]> => {
      const { data } = await axiosInstance.get<{ items: InboxNotification[] }>(API_BASE, {
        params: { limit },
      });
      return data.items;
    },
  });
}

export function useInboxUnreadCount() {
  return useQuery({
    queryKey: notificationsInboxKeys.unreadCount(),
    queryFn: async (): Promise<number> => {
      const { data } = await axiosInstance.get<{ total_unread: number }>(
        `${API_BASE}/unread-count`,
      );
      return data.total_unread;
    },
    refetchInterval: 60000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string): Promise<void> => {
      await axiosInstance.post(`${API_BASE}/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsInboxKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      const { data } = await axiosInstance.post<{ updated: number }>(
        `${API_BASE}/mark-all-read`,
      );
      return data.updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsInboxKeys.all });
    },
  });
}
